import React, { Fragment, useContext, useState, useRef, useEffect } from 'react';
import { usePageVisibility } from 'react-page-visibility';
import { Link } from 'react-router-dom';
import { toJS } from 'mobx';
import { useObserver } from 'mobx-react-lite';
import { ScrollView } from 'devextreme-react';
import { Tooltip } from 'devextreme-react/tooltip';
import DataGrid, { Sorting, Scrolling, Column } from 'devextreme-react/data-grid';
import { toast } from 'react-toastify';
import moment from 'moment';

import NotAvailableModal from './NotAvailableModal';
import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import CommunicationModal from '../../communications/CommunicationModal/_index';
import { renderBalance, getPurchaseCss } from './_index';
import DisabledOverlay from '../../_shared/DisabledOverlay';
import LoadingOverlay from '../../_shared/LoadingOverlay';
import { quickDrawerFocus } from '../../_shared/QuickDrawer';

import useSignalR from '../../../hooks/useSignalR';
import PurchaseStore from '../../../../stores/PurchaseViewStore';
import PurchaseUpdateStore from '../../../../stores/PurchaseUpdateStore';
import WorkOrderCreateStore from '../../../../stores/WorkOrderCreateStore';
import GroupPaymentCreateStore from '../../../../stores/GroupPaymentCreateStore';
import CommunicationStore from '../../../../stores/CommunicationStore';
import FileViewerStore from '../../../../stores/FileViewerStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as AccessType from '../../../../constants/accessTypes';
import * as rts from '../../../../constants/routes';
import * as fn from '../../../../utilities/_functions';
import * as bh from '../../../../utilities/badgeHelper';
import * as ih from '../../../../utilities/invoiceHelper';
import * as wh from '../../../../utilities/workOrderHelper';
import * as ah from '../../../../utilities/accessHelper';
import * as oh from '../../../../utilities/operationHelper';
import * as uh from '../../../../utilities/userHelper';
import * as sys from '../../../../utilities/systemHelper';
import api from '../../../../api';

function ViewPurchaseContent(props) {
    const signalR = useSignalR();
    const isMounted = useRef(true);
    const focusTimer = useRef(null);
    const isPageVisible = useRef(null);
    const communicationModalRef = useRef(null);
    const confirmModalRef = useRef(null);
    const pageVisibility = usePageVisibility();
    const purchase = useContext(PurchaseStore);
    const updatePurchase = useContext(PurchaseUpdateStore);
    const newGroupPayment = useContext(GroupPaymentCreateStore);
    const newWorkOrder = useContext(WorkOrderCreateStore);
    const communication = useContext(CommunicationStore);
    const fileViewer = useContext(FileViewerStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);
    const [hasSaveConflicts, setHasSaveConflicts] = useState(false);
    const [forceReload, setForceReload] = useState(false);
    const [pendingPaymentIndex, setPendingPaymentIndex] = useState(0);

    useEffect(() => {
        signalR.on('WorkOrder', (updated) => {
            if (updated && purchase.data && purchase.data.id === updated.purchaseId) {
                if (isPageVisible.current) {
                    purchase.reload();
                } else if (isMounted.current) {
                    setForceReload(true);
                }
            }
        });
        signalR.on('Payment', (updated) => {
            if (updated && purchase.data && purchase.data.customerId === updated.customerId) {
                if (isPageVisible.current) {
                    purchase.reload();
                } else if (isMounted.current) {
                    setForceReload(true);
                }
            }
        });

        if (purchase.startWorkOrder === true) {
            setTimeout(() => {
                if (purchase.data) {
                    handleStartWorkOrder();
                }
            })
        }

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    useEffect(() => {
        if (pageVisibility && forceReload) {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        setForceReload(false);
                    }
                })
        }
        isPageVisible.current = pageVisibility;
    }, [pageVisibility]) // eslint-disable-line

    const handleClose = () => {
        props.onClose();
    }

    const handleStartWorkOrder = () => {
        if (sys.configuration.workOrder.autoWorkOrderCreate) {
            if (purchase.stagedWorkOrders && purchase.stagedWorkOrders.length > 0) {
                confirmModalRef.current.show({
                    icon: <i className='fal fa-inbox text-info mr-2'></i>,
                    message: <>Would you like to start&nbsp;{(purchase.stagedWorkOrders.length === 1 ? 'a new work order' : 'new work orders')}?</>,
                    option1ClassName: 'btn btn-info shadow-0 bootbox-accept',
                    onOption1Click: handleStartStagedWorkOrderConfirmed,
                    onCancel: handleCancelStartStagedWorkOrder
                })
            }
            else {
                newWorkOrder.initialize(purchase.data.customerId, purchase.data.id)
                    .then(() => {
                        if (isMounted.current) {
                            if (newWorkOrder.data && newWorkOrder.data.length > 0) {
                                purchase.startWorkOrder = false;
                                confirmModalRef.current.show({
                                    icon: <i className='fal fa-inbox text-info mr-2'></i>,
                                    message: <>Would you like to start&nbsp;{(newWorkOrder.data.length === 1 ? 'a new work order' : 'new work orders')}?</>,
                                    option1ClassName: 'btn btn-info shadow-0 bootbox-accept',
                                    onOption1Click: handleStartWorkOrderConfirmed,
                                    onCancel: handleCancelStartWorkOrder
                                })
                            }
                        }
                    })
            }
        }
    }

    const handleStartStagedWorkOrderConfirmed = (event) => {
        purchase.convertStagedWorkOrders()
            .then(() => {
                updatePurchase.reload();
                confirmModalRef.current.close();
            })
    }

    const handleCancelStartStagedWorkOrder = (event) => {
        confirmModalRef.current.close();
    }

    const handleStartWorkOrderConfirmed = (event) => {
        if (newWorkOrder.data) {
            if (newWorkOrder.data.length === 1) {
                newWorkOrder.save()
                    .then(() => {
                        updatePurchase.reload();
                    })
            }
            else if (newWorkOrder.data.length > 1) {
                quickDrawer.activateQuickDrawer('order', 'create', null, handleNewWorkOrderSuccess, handleNewWorkOrderCancel)
                    .then(drawer => {
                        if (isMounted.current) {
                            focusTimer.current = setTimeout(() => {
                                quickDrawerFocus(drawer);
                            }, 100);

                            props.onEnableTab(false);
                        }
                    });

                props.onEnableTab(false);
            }
        }
        confirmModalRef.current.close();
    }

    const handleCancelStartWorkOrder = (event) => {
        confirmModalRef.current.close();
    }

    const handleOpenRefundedPurchase = (event, id) => {
        purchase.load(id);
    }

    const handleVoidAdjustment = (event) => {        
        confirmModalRef.current.show({
            icon: <i className={`${oh.getIcon('invoice')} text-danger mr-2`}></i>,
            message: <>Continue to create a sales clawback for {purchase.data ? uh.getDisplayFullNameById(purchase.data.userId) : ''}?</>,
            option1ClassName: 'btn btn-danger shadow-0 bootbox-accept',
            option1Text: 'Create clawback',
            cancelText: 'Cancel',
            onOption1Click: () => { handleVoidAdjustmentConfirm() },
            onCancel: () => { confirmModalRef.current.close(); }
        })
    }

    const handleVoidAdjustmentConfirm = () => {
        purchase.voidAdjustment()
            .then(() => {
                confirmModalRef.current.close();
            })
    }

    const handlePayment = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        quickDrawer.activateQuickDrawer('purchase', 'payment', null, handlePaymentSuccess, handlePaymentCancel)
                            .then(drawer => {
                                if (isMounted.current) {
                                    focusTimer.current = setTimeout(() => {
                                        quickDrawerFocus(drawer);
                                    }, 100);
                                }
                            })

                        props.onEnableTab(false);
                    }
                });
        }
    }

    const handlePaymentSuccess = (result) => {
        if (result && result.updated) {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        updatePurchase.clear();
                        toast.dark(() => <p data-pymt-ctd>Payment created.</p>);

                        if (purchase.data && purchase.data.remainingBalance <= 0 && result.data) {
                            handleConfirmEmailPrintInvoices([result.data]);
                        }
                    }
                })
        }
        props.onEnableTab(true);
    }

    const handlePaymentCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleStartGroupPayment = () => {
        if (isMounted.current) {
            newGroupPayment.initialize([purchase.data.customerId]);
            quickDrawer.activateQuickDrawer('purchase', 'group-payment', null, handleNewGroupPaymentSuccess, handleNewGroupPaymentCancel)
                .then(drawer => {
                    if (isMounted.current) {
                        focusTimer.current = setTimeout(() => {
                            quickDrawerFocus(drawer);
                        }, 100);
                    }
                });

            props.onEnableTab(false);
        }
    }

    const handleNewGroupPaymentSuccess = (result) => {
        if (result && result.updated) {
            purchase.reload();
            if (result.data) {
                const payments = Array.isArray(result.data) ? result.data : [result.data];
                handleConfirmEmailPrintInvoices(payments);
            }
        }

        newGroupPayment.clear();
        props.onEnableTab(true);
    }

    const handleNewGroupPaymentCancel = () => {
        newGroupPayment.clear();
        props.onEnableTab(true);
    }

    const handlePaymentRequest = () => {
        if (isMounted.current) {
            updatePurchase.load(purchase.id, true)
                .then(() => {
                    quickDrawer.activateQuickDrawer('purchase', 'payment-request', null, handlePaymentRequestSuccess, handlePaymentRequestCancel)
                        .then(drawer => {
                            if (isMounted.current) {
                                focusTimer.current = setTimeout(() => {
                                    quickDrawerFocus(drawer);
                                }, 100);
                            }
                        });

                    props.onEnableTab(false);
                })
        }
    }

    const handlePaymentRequestSuccess = (result) => {
        if (result && result.updated) {
            purchase.reload();

            if (result.data) {
                if (result.action === 'email') {
                    setIsGeneratingPrint(true);
                    ih.paymentRequest(purchase.data, result.data, communication, isGeneratingPrint)
                        .then(() => {
                            setIsGeneratingPrint(false);
                            communicationModalRef.current.show('email');
                        });
                }
                if (result.action === 'copy' && result.data.url) {
                    try {
                        const { ClipboardItem } = window;
                        const htmlContent = `<a href="${result.data.url}" target="_blank" rel="noopener noreferrer">Secure Payment Link</a>`;
                        const blob = new Blob([htmlContent], { type: 'text/html' });
                        const clipboardItem = new ClipboardItem({ 'text/html': blob });

                        navigator.clipboard.write([clipboardItem])
                            .then(() => {
                                toast.dark(() => <p>Copied to clipboard.</p>, { position: 'top-center' });
                            })
                    }
                    catch (err) {
                        toast.error(() => <p>Cannot copy to clipboard.</p>, { position: 'top-center' });
                    }
                }
            }
        }

        newGroupPayment.clear();
        props.onEnableTab(true);
    }

    const handlePaymentRequestCancel = () => {
        newGroupPayment.clear();
        props.onEnableTab(true);
    }

    const handleConfirmEmailPrintInvoices = (payments) => {
        confirmModalRef.current.show({
            icon: <i className={`${oh.getIcon('invoice')} text-primary mr-2`}></i>,
            message: <>What would you like to print or email the payment {(payments && payments.length > 1 ? 'receipts' : 'receipt')}?</>,
            option1ClassName: 'btn btn-primary shadow-0 bootbox-accept',
            option1Text: 'Print',
            option2ClassName: 'btn btn-info shadow-0 bootbox-accept',
            option2Text: 'Email',
            cancelText: 'Cancel',
            onOption1Click: () => { handlePrintPaymentReceipts(payments) },
            onOption2Click: () => { handleEmailPaymentReceipts(payments); },
            onCancel: () => { confirmModalRef.current.close(); }
        })
    }

    const handlePrintPaymentReceipts = (payments) => {
        confirmModalRef.current.close();

        if (payments && payments.length > 0) {
            api.Purchases.list(
                {
                    parameters: [
                        { field: 'Id', value: payments.map(p => { return p.id }).join(','), operator: 'contains' }
                    ],
                    includeTotalCount: false
                })
                .then(({ data }) => {
                    if (data && data.result && data.result.length > 0) {
                        ih.printBatch(data.result, setIsGeneratingPrint);
                    }
                })
        }
    }

    const handleEmailPaymentReceipts = (payments) => {
        confirmModalRef.current.close();

        if (payments && payments.length > 0) {
            api.Purchases.list(
                {
                    parameters: [
                        { field: 'Id', value: payments.map(p => { return p.id }).join(','), operator: 'contains' }
                    ],
                    includeTotalCount: false
                })
                .then(({ data }) => {
                    if (data && data.result && data.result.length > 0) {
                        setIsGeneratingPrint(true);

                        ih.emailBatch(data.result, communication)
                            .then(() => {
                                setIsGeneratingPrint(false);
                                toast.dark(() => <p>Email(s) sent.</p>);
                            });
                    }
                })
        }
    }

    const handleRefundPayment = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        quickDrawer.activateQuickDrawer('purchase', 'refund-payment', null, handleRefundPaymentSuccess, handleRefundPaymentCancel)
                            .then(drawer => {
                                if (isMounted.current) {
                                    focusTimer.current = setTimeout(() => {
                                        quickDrawerFocus(drawer);
                                    }, 100);
                                }
                            });

                        props.onEnableTab(false);
                    }
                })
        }
    }

    const handleRefundPaymentSuccess = () => {
        purchase.reload()
            .then(() => {
                if (isMounted.current) {
                    updatePurchase.clear();
                    toast.dark(() => <p data-refund-ctd>Refund created.</p>);
                }
            })
        props.onEnableTab(true);
    }

    const handleRefundPaymentCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleQuote = event => {
        event.preventDefault();

        updatePurchase.clear();
        updatePurchase.load(purchase.id)
            .then(() => {
                if (isMounted.current) {
                    updatePurchase.complete(true)
                        .then(() => {
                            if (isMounted.current) {
                                purchase.reload();
                            }
                        })
                        .catch(() => {
                            if (isMounted.current) {
                                setHasSaveConflicts(true);
                            }
                        })
                }
            })
    }

    const handleComplete = event => {
        event.preventDefault();

        updatePurchase.clear();
        updatePurchase.load(purchase.id)
            .then(() => {
                if (isMounted.current) {
                    updatePurchase.complete(true)
                        .then(() => {
                            if (isMounted.current) {
                                purchase.reload()
                                    .then(() => {
                                        setTimeout(() => {
                                            handleStartWorkOrder();
                                        }, 300);
                                    })
                            }
                        })
                        .catch(() => {
                            if (isMounted.current) {
                                setHasSaveConflicts(true);
                            }
                        })
                }
            })
    }

    const handleModeChange = mode => {
        updatePurchase.load(purchase.id)
            .then(() => {
                if (mode === 'update') {
                    updatePurchase.resetDefaultTransactionItem();
                    // TODO: hack? resetDefaultTransactionItem set hasUnsavedChanges => true
                    updatePurchase.hasUnsavedChanges = false;
                }
            })
        props.onModeChange(mode);
    }

    const handleDelete = event => {
        event.preventDefault();
        updatePurchase.load(purchase.id)
            .then(() => {
                updatePurchase.delete(true)
                    .then(() => {
                        if (isMounted.current) {
                            purchase.clear();
                            if (fn.isFunction(props.onClose)) {
                                props.onClose();
                            }
                        }
                    })
            })
    }

    const handleDeleteQuote = event => {
        event.preventDefault();
        confirmModalRef.current.show({
            icon: <i className='fal fa-trash-alt text-danger mr-2'></i>,
            message: <>Continue to delete&nbsp;<strong>Quote # {(purchase.data ? purchase.data.number : '')}</strong>?</>,
            option1ClassName: 'btn btn-danger shadow-0 bootbox-accept',
            onOption1Click: handleDeleteQuoteConfirmed,
            onCancel: handleDeleteQuoteCancel
        })
    }

    const handleDeleteQuoteConfirmed = event => {
        handleDelete(event);
        confirmModalRef.current.close();
    }

    const handleDeleteQuoteCancel = event => {
        event.preventDefault();
        confirmModalRef.current.close();
    }

    const handleUpdateCost = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        quickDrawer.activateQuickDrawer('purchase', 'update-cost', null, handlePurchaseUpdateCostSuccess, handlePurchaseUpdateCostCancel)
                            .then(drawer => {
                                if (isMounted.current) {
                                    focusTimer.current = setTimeout(() => {
                                        quickDrawerFocus(drawer);
                                    }, 100);
                                }
                            });

                        props.onEnableTab(false);
                    }
                })
        }
    }

    const handlePurchaseUpdateCostSuccess = (result) => {
        if (result && result.updated) {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        toast.dark(() => <p data-inv-cost-upated>Invoice cost updated.</p>);
                        updatePurchase.clear();
                    }
                });
        }
        props.onEnableTab(true);
    }

    const handlePurchaseUpdateCostCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleRefundPurchase = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        updatePurchase.refundPurchase(true)
                            .then(() => {
                                if (isMounted.current) {
                                    quickDrawer.activateQuickDrawer('purchase', 'refund-purchase', null, handleRefundPurchaseSuccess, handleRefundPurchaseCancel, handleRefundPurchaseError)
                                        .then(drawer => {
                                            if (isMounted.current) {
                                                focusTimer.current = setTimeout(() => {
                                                    quickDrawerFocus(drawer);
                                                }, 100);
                                            }
                                        });

                                    props.onEnableTab(false);
                                }
                            })
                    }
                })
        }
    }

    const handleRefundPurchaseSuccess = ({ requiredVerification, data }) => {
        if (requiredVerification) {
            quickDrawer.activateQuickDrawer('purchase', 'verify-returns', { action: 'refund' }, handleVerifyRefundReturnsSuccess, handleVerifyRefundReturnsCancel)
                .then(drawer => {
                    if (isMounted.current) {
                        focusTimer.current = setTimeout(() => {
                            quickDrawerFocus(drawer);
                        }, 100);
                    }
                });

            props.onEnableTab(false);
        }
        else {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        toast.dark(() => <p data-inv-refunded>Invoice refunded.</p>);
                        updatePurchase.clear();
                    }
                })
            props.onEnableTab(true);
        }
    }

    const handleVerifyRefundReturnsSuccess = (result) => {
        if (result.updated) {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        toast.dark(() => <p data-inv-refunded>Invoice refunded.</p>);
                        updatePurchase.clear();
                    }
                })
        } else {
            handleModeChange('view');
        }

        props.onEnableTab(true);
    }

    const handleVerifyRefundReturnsCancel = event => {
        updatePurchase.clear();
        handleModeChange('view');
    }

    // const handleRefundPurchaseSuccess = () => {
    //     purchase.reload()
    //         .then(() => {
    //             if (isMounted.current) {
    //                 toast.dark(() => <p data-txn-refunded>Transaction refunded.</p>);
    //             }
    //         })
    // }

    const handleRefundPurchaseCancel = () => {
    }

    const handleRefundPurchaseError = () => {
    }

    const handleNoChargePurchase = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        quickDrawer.activateQuickDrawer('purchase', 'no-charge-purchase', null, handleNoChargePurchaseSuccess, handleNoChargePurchaseCancel)
                            .then(drawer => {
                                if (isMounted.current) {
                                    focusTimer.current = setTimeout(() => {
                                        quickDrawerFocus(drawer);
                                    }, 100);
                                }
                            });

                        props.onEnableTab(false);
                    }
                })
        }
    }

    const handleNoChargePurchaseSuccess = () => {
        purchase.reload()
            .then(() => {
                if (isMounted.current) {
                    purchase.startWorkOrder = sys.configuration.workOrder.autoWorkOrderCreate;
                    toast.dark(() => <p data-txn-upd>Transaction updated.</p>);
                }
            })

        props.onEnableTab(true);
    }

    const handleNoChargePurchaseCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleUncollectiblePurchase = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        updatePurchase.uncollectiblePurchase(true)
                            .then(() => {
                                if (isMounted.current) {
                                    quickDrawer.activateQuickDrawer('purchase', 'uncollectible-purchase', null, handleUncollectiblePurchaseSuccess, handleUncollectiblePurchaseCancel)
                                        .then(drawer => {
                                            if (isMounted.current) {
                                                focusTimer.current = setTimeout(() => {
                                                    quickDrawerFocus(drawer);
                                                }, 100);
                                            }
                                        });

                                    props.onEnableTab(false);
                                }
                            })
                    }
                })
        }
    }

    const handleUncollectiblePurchaseSuccess = () => {
        purchase.reload()
            .then(() => {
                if (isMounted.current) {
                    toast.dark(() => <p data-txn-upd>Transaction updated.</p>);
                }
            })
        props.onEnableTab(true);
    }

    const handleUncollectiblePurchaseCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleVoidPurchase = () => {
        if (ah.check(AccessType.UPDATE_PURCHASE)) {
            updatePurchase.load(purchase.id)
                .then(() => {
                    if (isMounted.current) {
                        updatePurchase.voidPurchase(true)
                            .then(() => {
                                if (isMounted.current) {
                                    quickDrawer.activateQuickDrawer('purchase', 'void-purchase', null, handleVoidPurchaseSuccess, handleVoidPurchaseCancel, handleVoidPurchaseError)
                                        .then(drawer => {
                                            if (isMounted.current) {
                                                focusTimer.current = setTimeout(() => {
                                                    quickDrawerFocus(drawer);
                                                }, 100);
                                            }
                                        });

                                    props.onEnableTab(false);
                                }
                            })
                    }
                })
        }
    }

    const handleVoidPurchaseSuccess = ({ requiredVerification, data }) => {
        if (requiredVerification) {
            quickDrawer.activateQuickDrawer('purchase', 'verify-returns', { action: 'void' }, handleVerifyVoidReturnsSuccess, handleVerifyVoidReturnsCancel)
                .then(drawer => {
                    if (isMounted.current) {
                        focusTimer.current = setTimeout(() => {
                            quickDrawerFocus(drawer);
                        }, 100);
                    }
                });

            props.onEnableTab(false);
        }
        else {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        toast.dark(() => <p data-txn-voided>Transaction voided.</p>);
                        updatePurchase.clear();
                    }
                })
            props.onEnableTab(true);
        }
    }

    const handleVoidPurchaseCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleVoidPurchaseError = () => {
        props.onEnableTab(true);
    }

    const handleVerifyVoidReturnsSuccess = (result) => {
        if (result.updated) {
            purchase.reload()
                .then(() => {
                    if (isMounted.current) {
                        toast.dark(() => <p data-txn-voided>Transaction voided.</p>);
                        updatePurchase.clear();
                    }
                })
        } else {
            handleModeChange('view');
        }
    }

    const handleVerifyVoidReturnsCancel = event => {
        updatePurchase.clear();
        handleModeChange('view');
    }

    const handleWorkOrders = event => {
        if (purchase.data && purchase.data.workOrders && purchase.data.workOrders.length > 0) {
            quickDrawer.activateQuickDrawer('purchase', 'work-orders', null, handleWorkOrderClose, handleWorkOrderClose);
            props.onEnableTab(false);
        }
        else {
            newWorkOrder.initialize(purchase.data.customerId, purchase.id)
                .then(() => {
                    quickDrawer.activateQuickDrawer('order', 'create', null, handleNewWorkOrderSuccess, handleNewWorkOrderCancel)
                        .then(drawer => {
                            if (isMounted.current) {
                                focusTimer.current = setTimeout(() => {
                                    quickDrawerFocus(drawer);
                                }, 100);
                            }
                        });

                    props.onEnableTab(false);
                })
        }
    }

    const handleWorkOrderClose = () => {
        props.onEnableTab(true);
    }

    const handleNewWorkOrderSuccess = (event, result) => {
        if (result && result.updated) {
            if (result.data && result.data.length > 0) {
                toast.dark(() => <p data-wo-added>Work order(s) added.</p>);
            }
            else {
                toast.dark(() => <p data-wo-added>Work order added.</p>);
            }
        }
        newWorkOrder.clear();
        updatePurchase.reload();
        props.onEnableTab(true);
    }

    const handleNewWorkOrderCancel = (event) => {
        newWorkOrder.clear();
        props.onEnableTab(true);
    }

    const handlePaymentHistory = event => {
        quickDrawer.activateQuickDrawer('purchase', 'payment-history', null, handlePaymentHistoryClose, handlePaymentHistoryClose)
            .then(drawer => {
                if (isMounted.current) {
                    focusTimer.current = setTimeout(() => {
                        quickDrawerFocus(drawer);
                    }, 100);
                }
            });

        props.onEnableTab(false);
    }

    const handlePaymentHistoryClose = () => {
        props.onEnableTab(true);
    }

    const handleSaveConflictsCancel = () => {
        setHasSaveConflicts(false);
    }

    const handleCollectThisClick = (event, insurancePayment) => {
        if (insurancePayment) {
            const { id, data } = insurancePayment;

            updatePurchase.load(purchase.id);
            quickDrawer.activateQuickDrawer('purchase', 'private-insurance-payment', { id, data }, handleCollectThisSuccess, handleCollectThisCancel)
                .then(drawer => {
                    if (isMounted.current) {
                        focusTimer.current = setTimeout(() => {
                            quickDrawerFocus(drawer);
                        }, 100);
                    }
                });

            props.onEnableTab(false);
        }
    }

    const handleCollectThisSuccess = () => {
        purchase.reload()
            .then(() => {
                if (isMounted.current) {
                    updatePurchase.clear();

                    if (fn.isFunction(props.onCancel)) {
                        props.onCancel();
                    }
                }
            })
        props.onEnableTab(true);
    }

    const handleCollectThisCancel = () => {
        updatePurchase.clear();
        props.onEnableTab(true);
    }

    const handleViewInvoice = event => {
        ih.view(purchase.data, fileViewer, setIsGeneratingPrint);
    }

    const handleDownloadInvoice = event => {
        ih.download(purchase.data, setIsGeneratingPrint);
    }

    const handleEmailInvoice = (event) => {
        ih.email(purchase.data, communication, setIsGeneratingPrint)
            .then(() => {
                communicationModalRef.current.show('email');
            })
    }

    const handlePrintInvoice = event => {
        ih.print(purchase.data, setIsGeneratingPrint);
    }

    const handleRegenerateInvoice = event => {
        ih.regenerate(purchase.data, fileViewer, handleRegeneratingPrint);
    }

    const handleRegeneratingPrint = (inProgress) => {
        if (!inProgress) {
            toast.dark(() => <p data-inv-regen>Invoice regenerated.</p>);
        }

        setIsGeneratingPrint(inProgress)
    }

    const handlePendingPaymentPrevious = () => {
        if (isMounted.current) {
            let tempPendingPaymentIndex = pendingPaymentIndex;

            tempPendingPaymentIndex = tempPendingPaymentIndex - 1;

            if (tempPendingPaymentIndex < 0) {
                tempPendingPaymentIndex = (purchase.data.pendingPayments.length - 1);
            }

            setPendingPaymentIndex(tempPendingPaymentIndex);
        }
    }

    const handlePendingPaymentNext = () => {
        if (isMounted.current) {
            let tempPendingPaymentIndex = pendingPaymentIndex;

            tempPendingPaymentIndex = tempPendingPaymentIndex + 1;

            if (tempPendingPaymentIndex === purchase.data.pendingPayments.length) {
                tempPendingPaymentIndex = 0;
            }

            setPendingPaymentIndex(tempPendingPaymentIndex);
        }
    }

    const handleCommunicationSent = (event, result) => {
        if (result && result.type) {
            toast.dark(() => <p data-comm-sent><span className='tt-capitalize'>{result.type}</span> {result.scheduledSendDateTimeLocal ? 'scheduled' : 'sent'}.</p>);
        }
        else {
            toast.dark(() => <p data-comm-sent>Communication sent.</p>);
        }
        communicationModalRef.current.close();
    }

    const renderWorkOrder = (item) => {
        if (!purchase || !purchase.data || !purchase.data.workOrders) return;

        const orders = purchase.data.workOrders.filter(o => !o.isExpired && !o.isDeactivated && !wh.isStaged(o) && o.items.some(i => i.transactionItemIds.some(id => id === item.id)));

        if (orders && orders.length > 0) {
            const order = orders.sort((a, b) => moment.utc(a.createdDateUtc).isSameOrBefore(moment.utc(b.createdDateUtc)) ? 1 : -1)[0];
            const firstTransactionItem = purchase.data.items.filter(ti => order.items.some(oi => oi.transactionItemIds.some(id => id === ti.id))).sort((a, b) => { return a.displayOrder - b.displayOrder })[0];

            if (item && firstTransactionItem && firstTransactionItem.id === item.id) {
                return <>&nbsp;<span className='tt-normal text-nowrap'>(<Link to={`${rts.WorkOrders.View}/${order.id}`}>Work order # {order.number}</Link>)</span></>
            }
        }
    }

    const renderStockIcon = (item) => {
        if (!purchase || !purchase.data || !item) return <></>;

        switch (purchase.data.status) {
            case 'Incomplete':
            case 'Quote':
            case 'Completed':
                if (item.isStocked && item.isFromInventory) {
                    return <i className='fas fa-inbox-out text-success' title='Taken from stock'></i>;
                }
                break;

            case 'Refunded':
            case 'Voided':
                if (item.isReturnToInventory) {
                    if (item.isFromInventory) {
                        return <>
                            <i className='fas fa-inbox-out text-warning mr-2' title='Taken from stock'></i>
                            <i className='fas fa-inbox-in text-warning' title='Returned to stock'></i>
                        </>;
                    } else {
                        return <>
                            <i className='fas fa-inbox mr-1' title='Not taken from stock'></i>
                            <i className='fas fa-inbox-in text-warning' title='Returned to stock'></i>
                        </>;
                    }
                } else {
                    if (item.isFromInventory) {
                        return <>
                            <i className='fas fa-inbox-out text-danger mr-2' title='Taken from stock'></i>
                            <i className='fas fa-inbox text-danger' title='Not returned to stock'></i>
                        </>
                    }
                }
                break;

            default:
                break;
        }

        return <></>;
    }

    const renderTitle = () => {
        return <div className='popup-title'>
            <div className='actions left-actions'>
                <ul>
                    {renderDocumentActions()}
                    {renderHistoryActions()}
                </ul>
            </div>
            <div className='actions right-actions'>
                <ul>
                    {
                        ah.check(AccessType.UPDATE_PURCHASE) ?
                            <>
                                {renderPurchaseActions()}
                            </> : null
                    }
                    <li>
                        <button type='button' className='btn btn-icon btn-close' onClick={handleClose}>
                            <i className='close-icon fal fa-times fs-xl'></i>
                        </button>
                    </li>
                </ul>
            </div>
        </div>
    }

    const renderContent = () => {
        return <div data-view-purchase className='popup-body'>
            <ScrollView width='100%' height='100%'>
                {
                    purchase.data ?
                        <>
                            <div className='order-modal-header'>
                                <div className='order-header pt-2 px-4'>
                                    <div className='order-description'>
                                        <h3 className='purchase-title'>{purchase.data.isQuote ? 'Quote' : 'Invoice'} Detail</h3>
                                        <p>{purchase.data.isQuote ? 'Quote' : 'Invoice'} # <strong>{purchase.data.invoiceNumber}</strong></p>
                                        {
                                            !purchase.data.isQuote ?
                                                <div className={'order-status ' + getPurchaseCss(purchase)}>
                                                    <p className='title'>Status</p>
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <td>Invoice</td>
                                                                <td>
                                                                    {bh.renderPurchaseBalanceStatus(purchase.data, 'text-truncate text-truncate-md fs-sm p-2')}
                                                                </td>
                                                            </tr>
                                                            {renderBalance(purchase)}
                                                        </tbody>
                                                    </table>
                                                </div> :
                                                <>
                                                    {
                                                        purchase.data.quoteExpiryDate ?
                                                            <>
                                                                {
                                                                    purchase.data.isQuoteExpired ?
                                                                        <p className='text-danger'>Expired on <strong>{moment(purchase.data.quoteExpiryDate).format('MMMM D, YYYY')}</strong></p> :
                                                                        <p>Expires on <strong>{moment(purchase.data.quoteExpiryDate).format('MMMM D, YYYY')}</strong></p>
                                                                }
                                                            </> : <></>
                                                    }
                                                </>
                                        }
                                    </div>
                                    {
                                        !purchase.data.isQuote ?
                                            <>
                                                {renderPendingPayments()}
                                                {
                                                    purchase.data.shippingAddress ?
                                                        <div className='order-extra'>
                                                            <p className='title'>Shipping Address</p>
                                                            <address>
                                                                {
                                                                    purchase.data.shipToName ?
                                                                        <p><strong>{purchase.data.shipToName}</strong></p> : null
                                                                }
                                                                {
                                                                    purchase.data.shippingAddress && purchase.data.shippingAddress.addressLine1 ?
                                                                        <>
                                                                            {
                                                                                purchase.data.shippingAddress.line2 ?
                                                                                    <p>{purchase.data.shippingAddress.addressLine1}, {purchase.data.shippingAddress.line2}</p> :
                                                                                    <p>{purchase.data.shippingAddress.addressLine1}</p>
                                                                            }
                                                                        </> : null

                                                                }
                                                                {
                                                                    purchase.data.shippingAddress && purchase.data.shippingAddress.addressLine2 ?
                                                                        <p>{purchase.data.shippingAddress.addressLine2}</p> : null
                                                                }
                                                                {
                                                                    purchase.data.shippingAddress && purchase.data.shippingAddress.country ?
                                                                        <p>{purchase.data.shippingAddress.country}</p> : null
                                                                }
                                                            </address>
                                                        </div> : null
                                                }
                                            </> : <></>
                                    }
                                    {
                                        purchase.data.customer ?
                                            <div className='order-extra'>
                                                <p className='title'>Billing Information</p>
                                                <>
                                                    <p className='name'>{purchase.data.customerName}</p>
                                                    {
                                                        purchase.data.billingAddress || purchase.data.customerEmail || purchase.data.customerPhoneNumber ?
                                                            <address>
                                                                {
                                                                    purchase.data.billingAddress && purchase.data.billingAddress.addressLine1 ?
                                                                        <p>{purchase.data.billingAddress.addressLine1}</p> : null
                                                                }
                                                                {
                                                                    purchase.data.billingAddress && purchase.data.billingAddress.addressLine2 ?
                                                                        <p>{purchase.data.billingAddress.addressLine2}</p> : null
                                                                }
                                                                {
                                                                    purchase.data.billingAddress && purchase.data.billingAddress.country ?
                                                                        <p>{purchase.data.billingAddress.country}</p> : null
                                                                }
                                                                {
                                                                    purchase.data.customerEmail ?
                                                                        <p><a href={`mailto:${purchase.data.customerEmail}`}>{purchase.data.customerEmail}</a></p> : null
                                                                }
                                                                {
                                                                    purchase.data.customerPhoneNumber ?
                                                                        <p><a href={`tel:${purchase.data.customerPhoneNumber}`}>{purchase.data.customerPhoneNumber}</a></p> : null
                                                                }
                                                            </address> : null
                                                    }
                                                </>
                                            </div> : null
                                    }
                                </div>
                                {
                                    !purchase.data.isQuote ?
                                        <div className='order-header pb-2 px-4'>
                                            <div className='order-description'>
                                                <table>
                                                    <tbody>
                                                        <tr>
                                                            <td><span className='title'>{purchase.data.isQuote ? 'Quote' : 'Invoice'} Date</span></td>
                                                            <td>{(purchase.data.invoiceDate ? moment(purchase.data.invoiceDate) : (purchase.data.completedDateUtc ? moment.utc(purchase.data.completedDateUtc).local() : moment())).format('MMMM D, YYYY')}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><span className='title'>Due Date</span></td>
                                                            <td>{(purchase.data.dueDate ? moment(purchase.data.dueDate) : moment()).format('MMMM D, YYYY')}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <>
                                                {
                                                    purchase.data.insuranceInformation && JSON.stringify(purchase.data.insuranceInformation) !== JSON.stringify({}) ?
                                                        <div className='order-extra'></div> : null
                                                }
                                                {
                                                    purchase.data.shippingAddress ?
                                                        <div className='order-extra'></div> : null
                                                }
                                                {
                                                    purchase.data.customer ?
                                                        <>
                                                            <div className='order-extra'>
                                                                <p className='title'>Licensed Practitioner</p>
                                                                <p>
                                                                    {
                                                                        purchase.data.user ?
                                                                            <><strong>{purchase.data.user.fullName}</strong>{purchase.data.user.licenseNumber ? <span className='ml-1'>(No.: {purchase.data.user.licenseNumber})</span> : null}</> :
                                                                            <span className='text-gray-600'>None</span>
                                                                    }
                                                                </p>
                                                                {
                                                                    purchase.data.userAdjustments && purchase.data.userAdjustments.length > 0 ?
                                                                        <>
                                                                            <p id={`user-adjustment-${purchase.data.id}`} className='mb-0 cursor-pointer text-info'>Adjustment history</p>
                                                                            <Tooltip
                                                                                target={`#user-adjustment-${purchase.data.id}`}
                                                                                position='bottom'
                                                                                showEvent="dxhoverstart"
                                                                                hideEvent="dxhoverend"
                                                                            >
                                                                                <table className='fs-xs text-white text-left'>
                                                                                    <tbody>
                                                                                        {
                                                                                            purchase.data.userAdjustments.map((a, ai) => {
                                                                                                return <Fragment key={`purchase_user_adjustment_${ai}`}><tr>
                                                                                                    <td valign='top' className='p-h'>
                                                                                                        <span className='text-nowrap'>{sys.getFormattedShortDate(moment.utc(a.createdDateUtc).local(), true)}</span>
                                                                                                    </td>
                                                                                                    <td valign='top' className='p-h'>
                                                                                                        <span className='text-nowrap text-strike'>{uh.getDisplayShortNameById(a.fromUserId)}</span>
                                                                                                    </td>
                                                                                                    <td valign='top' className='p-o'>
                                                                                                        <span className='text-nowrap fw-500'>{uh.getDisplayShortNameById(a.toUserId)}</span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                                </Fragment>
                                                                                            })
                                                                                        }
                                                                                    </tbody>
                                                                                </table>
                                                                            </Tooltip>
                                                                        </> : <></>
                                                                }
                                                            </div>
                                                        </> : null
                                                }
                                            </>
                                        </div> : <></>
                                }
                            </div>
                            <div className='order-detail'>
                                <DataGrid
                                    keyExpr='id'
                                    width='100%'
                                    activeStateEnabled={false}
                                    focusStateEnabled={false}
                                    hoverStateEnabled={false}
                                    dataSource={purchase.data.items}
                                    showBorders={false}
                                    showRowLines={true}
                                    showColumnLines={false}
                                    allowColumnReordering={false}
                                    allowColumnResizing={false}
                                >
                                    <Scrolling rowRenderingMode='standard'></Scrolling>
                                    <Sorting mode='none' />
                                    <Column
                                        dataField='descriptor'
                                        caption='Description'
                                        alignment='left'
                                        width='40%'
                                        cellRender={({ data }) => {
                                            const key = `purchase-item-descriptor-${data.id}`;
                                            const descriptor = data.isOverridden ? data.overriddenDescriptor : data.descriptor;
                                            const productName = data.product && data.product.name ? data.product.name : ''
                                            const brandName = data.product && data.product.brandName ? data.product.brandName : ''
                                            const displayTooltip = data.product && data.product.brandName !== '[System]' && productName && productName !== descriptor;

                                            return <div className='d-flex h-100 w-100 align-items-center py-2' style={{ flexWrap: 'wrap' }}>
                                                {
                                                    data.transactionItemType !== 'Service' ?
                                                        <strong className='quantity text-lowercase'>{data.quantity}x&nbsp;&nbsp;</strong> : <></>
                                                }
                                                <span id={key} className={'text-truncate' + (displayTooltip ? ' text-info fw-500 tt-dashed-line-link' : '')}>{descriptor}</span>
                                                {
                                                    displayTooltip ?
                                                        <Tooltip
                                                            target={`#${key}`}
                                                            position='bottom'
                                                            showEvent="dxhoverstart"
                                                            hideEvent="dxhoverend"
                                                        >
                                                            <table className='fs-xs text-white text-left'>
                                                                <tbody>
                                                                    <tr>
                                                                        <td>{`${(brandName ? `${brandName} - ` : '')}${productName}`}</td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </Tooltip> : null
                                                }
                                                {renderWorkOrder(data)}
                                            </div>
                                        }}
                                    />
                                    <Column
                                        dataField='isStocked'
                                        caption='Stock'
                                        alignment='center'
                                        width='12%'
                                        cellRender={({ data }) => {
                                            return renderStockIcon(data)
                                        }}
                                    />
                                    <Column
                                        dataField='unitPrice'
                                        caption='Unit Price'
                                        alignment='right'
                                        width='12%'
                                        cellRender={({ data }) => {
                                            return fn.formatCurrency(data.unitPrice)
                                        }}
                                    />
                                    <Column
                                        dataField='discountAmount'
                                        caption='Discount'
                                        alignment='right'
                                        width='12%'
                                        cellRender={({ data }) => {
                                            return <>{fn.formatCurrency((data.discountAmount * -1.0), '--')}{data.discountAmount && data.discountRate ? <small className='ml-1'>({fn.formatPercent(data.discountRate)})</small> : null}</>
                                        }}
                                    />
                                    <Column
                                        dataField='isTaxable'
                                        caption={!purchase.taxRates || purchase.taxRates.length <= 1 ? 'Tax' : purchase.taxRates[0].code}
                                        alignment='right'
                                        width={!purchase.taxRates || purchase.taxRates.length <= 1 ? '12%' : `${12.0 / purchase.taxRates.length}%`}
                                        cellRender={({ data }) => {
                                            return <>{(data.isTaxable ? fn.formatPercent(data.taxRate1) : '--')}</>
                                        }}
                                    />
                                    <Column
                                        dataField='isTaxable2'
                                        caption={purchase.taxRates && purchase.taxRates.length > 1 ? purchase.taxRates[1].code : ''}
                                        alignment='right'
                                        width={!purchase.taxRates || purchase.taxRates.length <= 1 ? '12%' : `${12.0 / purchase.taxRates.length}%`}
                                        visible={purchase.taxRates && purchase.taxRates.length > 1}
                                        cellRender={({ data }) => {
                                            return <>{(data.isTaxable2 ? fn.formatPercent(data.taxRate2) : '--')}</>
                                        }}
                                    />
                                    <Column
                                        dataField='isTaxable3'
                                        caption={purchase.taxRates && purchase.taxRates.length > 2 ? purchase.taxRates[2].code : ''}
                                        alignment='right'
                                        width={!purchase.taxRates || purchase.taxRates.length <= 1 ? '12%' : `${12.0 / purchase.taxRates.length}%`}
                                        visible={purchase.taxRates && purchase.taxRates.length > 2}
                                        cellRender={({ data }) => {
                                            return <>{(data.isTaxable3 ? fn.formatPercent(data.taxRate3) : '--')}</>
                                        }}
                                    />
                                    <Column
                                        dataField='price'
                                        caption='Total'
                                        alignment='right'
                                        width='12%'
                                        cellRender={({ data }) => {
                                            const key = `purchase-item-discount-${data.id}`;
                                            return <>
                                                {
                                                    data.discountAmount || data.isTaxable || data.isTaxable2 || data.isTaxable3 ?
                                                        <div className='d-flex h-100 w-100 text-right align-items-center justify-content-end'>
                                                            <span
                                                                id={key}
                                                                className='fw-500 text-info tt-dashed-line-link cursor-pointer'

                                                            >
                                                                {fn.formatCurrency(data.price)}
                                                            </span>
                                                            <Tooltip
                                                                target={`#${key}`}
                                                                position='right'
                                                                showEvent="dxhoverstart"
                                                                hideEvent="dxhoverend"
                                                            >
                                                                <table className='fs-xs text-gray-500 text-left'>
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className='fw-500 pr-2'>Before</td>
                                                                            <td className='text-right'>{fn.formatCurrency(data.price)}</td>
                                                                        </tr>
                                                                        {
                                                                            data.discountAmount ?
                                                                                <tr>
                                                                                    <td className='fw-500 pr-2'>Discount ({(data.price % data.discountAmount) !== 0 ? '~' : ''}{Math.ceil((data.discountRate ? data.discountRate : data.discountAmount / data.price) * 100)}%)</td>
                                                                                    <td className='text-right'>{fn.formatCurrency(data.discountAmount)}</td>
                                                                                </tr> : null
                                                                        }
                                                                        {
                                                                            data.isTaxable ?
                                                                                <tr>
                                                                                    <td className='fw-500 pr-2'>Tax ({data.taxRate1 * 100}%)</td>
                                                                                    <td className='text-right'>{fn.formatCurrency((data.taxableAmount1 * data.taxRate1))}</td>
                                                                                </tr> : null
                                                                        }
                                                                        {
                                                                            data.isTaxable2 ?
                                                                                <tr>
                                                                                    <td className='fw-500 pr-2'>Tax ({data.taxRate2 * 100}%)</td>
                                                                                    <td className='text-right'>{fn.formatCurrency((data.taxableAmount2 * data.taxRate2))}</td>
                                                                                </tr> : null
                                                                        }
                                                                        {
                                                                            data.isTaxable3 ?
                                                                                <tr>
                                                                                    <td className='fw-500 pr-2'>Tax ({data.taxRate3 * 100}%)</td>
                                                                                    <td className='text-right'>{fn.formatCurrency((data.taxableAmount3 * data.taxRate3))}</td>
                                                                                </tr> : null
                                                                        }
                                                                        <tr>
                                                                            <td colSpan={2} className='fw-500 border-1 border-bottom text-white pt-1'></td>
                                                                        </tr>
                                                                        <tr>
                                                                            <td className='fw-500 pr-2 pt-1 border-1 border-top text-white'>After</td>
                                                                            <td className='fw-500 text-right pt-1 border-1 border-top text-white'>
                                                                                {
                                                                                    fn.formatCurrency(
                                                                                        (data.price - data.discountAmount) +
                                                                                        (data.isTaxable ? (data.taxableAmount1 * data.taxRate1) : 0.0) +
                                                                                        (data.isTaxable2 ? (data.taxableAmount2 * data.taxRate2) : 0.0) +
                                                                                        (data.isTaxable3 ? (data.taxableAmount3 * data.taxRate3) : 0.0)
                                                                                    )
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </Tooltip>
                                                        </div> :
                                                        <>{fn.formatCurrency(data.price)}</>
                                                }
                                            </>
                                        }}
                                    />
                                </DataGrid>
                                <table className='table-order-detail-bottom'>
                                    <colgroup>
                                        <col style={{ width: '38%' }}></col>
                                        <col style={{ width: '38%' }}></col>
                                        <col style={{ width: '12%' }}></col>
                                        <col style={{ width: '12%' }}></col>
                                    </colgroup>
                                    <tbody role='presentation'>
                                        <tr role='row' style={{ borderBottom: '1px solid #ddd' }}>
                                            <td
                                                rowSpan={3 + (!purchase.taxRates || purchase.taxRates.length <= 1 ? 1 : purchase.taxRates.length)}
                                                style={{ verticalAlign: 'top', borderRight: '1px solid #ddd' }}
                                            >
                                                <strong className='d-block text-gray-500 text-uppercase mt-3'>Note:</strong>
                                                {
                                                    purchase.data.noteHtml ? <>
                                                        <div className='p-2 html' dangerouslySetInnerHTML={{ __html: purchase.data.noteHtml }}></div>
                                                    </> : null
                                                }
                                            </td>
                                            <td
                                                rowSpan={3 + (!purchase.taxRates || purchase.taxRates.length <= 1 ? 1 : purchase.taxRates.length)}
                                                style={{ verticalAlign: 'top', borderRight: '1px solid #ddd' }}
                                            >
                                                <strong className='d-block text-gray-500 text-uppercase mt-3'>Internal Note:</strong>
                                                {
                                                    purchase.data.internalNoteHtml ? <>
                                                        <div className='p-2 html' dangerouslySetInnerHTML={{ __html: purchase.data.internalNoteHtml }}></div>
                                                    </> : null
                                                }
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>Subtotal</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>{fn.formatCurrency(purchase.data.subTotal)}</strong>
                                            </td>
                                        </tr>
                                        <tr role='row' style={{ borderBottom: '1px solid #ddd' }}>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>Discount</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {fn.formatCurrency(purchase.data.discountAmount * -1.0, '--')}
                                            </td>
                                        </tr>
                                        {
                                            !purchase.taxRates || purchase.taxRates.length <= 1 ?
                                                <tr role='row' style={{ borderBottom: '2px solid #ddd' }}>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <strong>Tax</strong>
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {fn.formatCurrency(purchase.data.totalTaxAmount)}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                    </td>
                                                </tr> :
                                                <>
                                                    {
                                                        purchase.taxRates.length > 1 ?
                                                            <>
                                                                <tr role='row' style={{ borderBottom: '2px solid #ddd' }}>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <strong>{purchase.taxRates[0].code}</strong>
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        {fn.formatCurrency(purchase.data.taxAmount1)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                    </td>
                                                                </tr>
                                                                <tr role='row' style={{ borderBottom: '2px solid #ddd' }}>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        <strong>{purchase.taxRates[1].code}</strong>
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                        {fn.formatCurrency(purchase.data.taxAmount2)}
                                                                    </td>
                                                                    <td style={{ textAlign: 'right' }}>
                                                                    </td>
                                                                </tr>
                                                                {
                                                                    purchase.taxRates.length > 2 ?
                                                                        <>
                                                                            <tr role='row' style={{ borderBottom: '2px solid #ddd' }}>
                                                                                <td style={{ textAlign: 'right' }}>
                                                                                    <strong>{purchase.taxRates[2].code}</strong>
                                                                                </td>
                                                                                <td style={{ textAlign: 'right' }}>
                                                                                    {fn.formatCurrency(purchase.data.taxAmount3)}
                                                                                </td>
                                                                                <td style={{ textAlign: 'right' }}>
                                                                                </td>
                                                                            </tr>
                                                                        </> : null
                                                                }
                                                            </> : null
                                                    }
                                                </>
                                        }
                                        <tr role='row' style={{ borderBottom: '2px solid #ddd' }}>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>Total</strong>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <strong>{fn.formatCurrency(purchase.data.total)}</strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </> : null
                }
            </ScrollView>
            <NotAvailableModal display={hasSaveConflicts} onCancel={handleSaveConflictsCancel} />
        </div>
    }

    const renderPendingPayments = () => {
        if (isMounted.current && purchase && purchase.data && purchase.data.pendingPayments && purchase.data.pendingPayments.length > 0 && purchase.data.pendingPayments.length > pendingPaymentIndex) {
            const selectedPendingPayment = toJS(purchase.data.pendingPayments[pendingPaymentIndex]);
            const payment = purchase.data.payments && purchase.data.payments.some(p => p.id === selectedPendingPayment.id) ? purchase.data.payments.filter(p => p.id === selectedPendingPayment.id)[0] : null
            let title = 'Payment Information';
            let content;

            switch (selectedPendingPayment.paymentMethod) {
                case 'PrivateInsurance':
                    const { data: insuranceInformation } = selectedPendingPayment;
                    title = 'Insurance Information';
                    content = <>
                        <p className='name text-truncate text-truncate-lg'>{insuranceInformation.provider}</p>
                        {
                            insuranceInformation.policyHolderName ?
                                <p className='info'>{insuranceInformation.policyHolderName}</p> : null
                        }
                        {
                            insuranceInformation.memberId ||
                                insuranceInformation.planNumber ||
                                insuranceInformation.accountNumber ||
                                insuranceInformation.policyNumber ?
                                <div className='info'>{[
                                    insuranceInformation.memberId,
                                    insuranceInformation.planNumber,
                                    insuranceInformation.accountNumber,
                                    insuranceInformation.policyNumber
                                ].filter(i => !!i).join(' - ')}</div> : null
                        }
                        {
                            insuranceInformation.amount ?
                                <p className='info fw-500'>{fn.formatCurrency(insuranceInformation.amount)}</p> : null
                        }
                        {
                            payment && payment.isToBeCollected ?
                                <button
                                    type='button'
                                    className='btn btn-sm btn-link text-right p-0 text-success text-success-800-hover'
                                    onClick={(e) => handleCollectThisClick(e, selectedPendingPayment)}
                                >
                                    Mark as collected
                                </button> : null
                        }
                    </>;
                    break;

                case 'GovernmentProgram':
                    const { data: governmentProgramInformation } = selectedPendingPayment;
                    title = 'Government Program';
                    content = <>
                        <p className='name'>{governmentProgramInformation.name}{(governmentProgramInformation.code ? ` (${governmentProgramInformation.code})` : '')}</p>
                        {
                            governmentProgramInformation.amount ?
                                <p className='info fw-500'>{fn.formatCurrency(governmentProgramInformation.amount)}</p> : null
                        }
                        {
                            payment && payment.isToBeCollected ?
                                <button
                                    type='button'
                                    className='btn btn-sm btn-link text-right p-0 text-success text-success-800-hover'
                                    onClick={(e) => handleCollectThisClick(e, selectedPendingPayment)}
                                >
                                    Mark as collected
                                </button> : null
                        }
                    </>;
                    break;

                default:
                    break;
            }

            return <div className='order-extra'>
                <div className='d-flex w-100 align-items-center'>
                    {
                        purchase.data.pendingPayments.length > 1 ?
                            <button
                                type='button'
                                className='btn fs-110 p-0 h-auto line-height-0 mb-1 ml-n3 mr-2'
                                onClick={handlePendingPaymentPrevious}
                            >
                                <i className='fas fa-caret-left'></i>
                            </button> : null
                    }
                    <p className='title'>{title}</p>
                    {
                        purchase.data.pendingPayments.length > 1 ?
                            <button
                                type='button'
                                className='btn fs-110 p-0 h-auto line-height-0 mb-1 ml-2'
                                onClick={handlePendingPaymentNext}
                            >
                                <i className='fas fa-caret-right'></i>
                            </button> : null
                    }
                </div>
                {content}
            </div>
        }

        return null;
    }

    const renderHistoryActions = () => {
        if (purchase.data) {
            switch (purchase.data.status) {
                case 'Incomplete':
                    break;

                case 'Quote':
                case 'Completed':
                case 'Refunded':
                case 'Uncollectible':
                case 'Voided':
                    return <>
                        <li>
                            <div className='btn-group'>
                                <button
                                    className='btn btn-icon'
                                    title={purchase.data.customerId ? 'Work order(s)' : 'Cannot create work order on anonymous sales'}
                                    disabled={!purchase.data.customerId || purchase.data.customer.isSystemCustomer}
                                    onClick={handleWorkOrders}
                                >
                                    <i className={oh.getIcon('work-order', 'default')}></i>
                                    {
                                        purchase.data && purchase.data.workOrders && purchase.data.workOrders.some(o => !o.isDeactivated && !o.isExpired && !wh.isStaged(o)) ?
                                            <span className={`badge badge-icon ml-n1`}>{purchase.data.workOrders.filter(o => !o.isDeactivated && !o.isExpired && !wh.isStaged(o)).length}</span> : null
                                    }
                                </button>
                                {
                                    ['Completed', 'Refunded', 'Uncollectible'].some(s => s === purchase.data.status) ?
                                        <button
                                            className='btn btn-icon'
                                            title='Payment history'
                                            onClick={handlePaymentHistory}
                                        >
                                            <i className={oh.getIcon('purchase', 'history')}></i>
                                            {
                                                purchase.data && purchase.data.payments && purchase.data.payments.length > 0 ?
                                                    <span className={`badge badge-icon ml-n1`}>{purchase.data.payments.length}</span> : null
                                            }
                                        </button> : <></>
                                }
                            </div>
                        </li>
                    </>;

                default:
                    break;
            }
        }
    }

    const renderDocumentActions = () => {
        if (purchase.data) {
            switch (purchase.data.status) {
                case 'Incomplete':
                case 'Voided':
                    break;

                case 'Quote':
                case 'Completed':
                case 'Refunded':
                case 'Uncollectible':
                    const invoiceQuoteText = purchase.data.isQuote ? 'quote' : 'invoice';

                    return <>
                        {
                            purchase.data.isPrinted ?
                                <li>
                                    <div className='btn-group'>
                                        <button
                                            className='btn btn-icon'
                                            title={`Regenerate ${invoiceQuoteText}`}
                                            onClick={handleRegenerateInvoice}
                                        >
                                            <i className='fab fa-sync text-primary'></i>
                                        </button>
                                    </div>
                                </li> : null
                        }
                        <li>
                            <div className='btn-group'>
                                <button
                                    className='btn btn-icon'
                                    title={`View ${invoiceQuoteText}`}
                                    onClick={handleViewInvoice}
                                >
                                    <i className='fal fa-file-search'></i>
                                </button>
                                <button
                                    className='btn btn-icon'
                                    title={`Download ${invoiceQuoteText}`}
                                    onClick={handleDownloadInvoice}
                                >
                                    <i className='fal fa-file-download'></i>
                                </button>
                                {
                                    purchase.data && purchase.data.customer ?
                                        <>
                                            <button
                                                className='btn btn-icon'
                                                title={`Email ${invoiceQuoteText}`}
                                                onClick={handleEmailInvoice}
                                            >
                                                <i className='fal fa-envelope'></i>
                                            </button>
                                        </> : null
                                }
                                <button
                                    className='btn btn-icon'
                                    title={`Print ${invoiceQuoteText}`}
                                    onClick={handlePrintInvoice}
                                >
                                    <i className='fal fa-print'></i>
                                </button>
                            </div>
                        </li>
                    </>;

                default:
                    break;
            }
        }
    }

    const renderPurchaseActions = () => {
        if (purchase.data) {
            switch (purchase.data.status) {
                case 'Incomplete':
                    return <>
                        <li>
                            <div className='btn-group'>
                                <button
                                    type='button'
                                    className='btn btn-alt px-3 dropdown-toggle'
                                    data-toggle='dropdown'
                                    aria-haspopup='true'
                                    aria-expanded='false'
                                >
                                    <span className='mr-1'>Change</span>
                                </button>
                                <div className='dropdown-menu dropdown-menu-right'>
                                    <button
                                        type='button'
                                        className='dropdown-item'
                                        onClick={() => handleModeChange('update')}
                                    >
                                        Update
                                    </button>
                                    <button
                                        type='button'
                                        className='dropdown-item text-danger fw-500'
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </li>
                        <li>
                            <div className='btn-group dropdown'>
                                <button
                                    type='button'
                                    className='btn btn-success px-3'
                                    onClick={e => handleComplete(e)}
                                >
                                    Invoice
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-success dropdown-toggle px-2'
                                >
                                </button>
                                <div className='dropdown-menu dropdown-menu-right min-width-auto'>
                                    <button
                                        type='button'
                                        className='dropdown-item'
                                        disabled={!purchase.data.customerId}
                                        onClick={handleNoChargePurchase}
                                    >
                                        No charge
                                    </button>
                                    <button
                                        type='button'
                                        className='dropdown-item'
                                        onClick={e => handleQuote(e)}
                                    >
                                        Save as Quote
                                    </button>
                                </div>
                            </div>
                        </li>
                    </>;

                case 'Quote':
                    return <>
                        <li>
                            <div className='btn-group'>
                                <button
                                    type='button'
                                    className='btn btn-alt px-3 dropdown-toggle'
                                    data-toggle='dropdown'
                                    aria-haspopup='true'
                                    aria-expanded='false'
                                >
                                    <span className='mr-1'>Change</span>
                                </button>
                                <div className='dropdown-menu dropdown-menu-right'>
                                    <button
                                        type='button'
                                        className='dropdown-item'
                                        onClick={() => handleModeChange('update')}
                                    >
                                        Update quote
                                    </button>
                                    <button
                                        type='button'
                                        className='dropdown-item text-danger fw-500'
                                        onClick={handleDeleteQuote}
                                    >
                                        Delete quote
                                    </button>
                                </div>
                            </div>
                        </li>
                        {
                            !purchase.data.isQuoteExpired ?
                                <li>
                                    <div className='btn-group dropdown'>
                                        <button
                                            type='button'
                                            className='btn btn-success px-3'
                                            onClick={e => handleComplete(e)}
                                        >
                                            Invoice
                                        </button>
                                        <button
                                            type='button'
                                            className='btn btn-success dropdown-toggle px-2'
                                        >
                                        </button>
                                        <div className='dropdown-menu dropdown-menu-right min-width-auto'>
                                            <button
                                                type='button'
                                                className='dropdown-item'
                                                disabled={!purchase.data.customerId}
                                                onClick={handleNoChargePurchase}
                                            >
                                                No charge
                                            </button>
                                        </div>
                                    </div>
                                </li> : null
                        }
                    </>;

                case 'Completed':
                    return !!purchase.data.refundPurchaseId ?
                        <>
                            <li>
                                <span className='fw-500 text-danger mr-2'>This invoice was refunded on {sys.getFormattedShortDate(moment.utc(purchase.data.refundedDateUtc).local())}.</span>
                            </li>
                            <li>
                                <button
                                    type='button'
                                    className='btn btn-danger px-3'
                                    onClick={(e) => { handleOpenRefundedPurchase(e, purchase.data.refundPurchaseId) }}
                                >
                                    Open Refunded Invoice
                                </button>
                            </li>
                        </> :
                        <>
                            {
                                purchase.data && purchase.data.remainingBalance > 0 && moment(purchase.data.invoiceDate).startOf('day').isSameOrBefore(moment().startOf('day')) ?
                                    <li>{renderPaymentButton()}</li> : null
                            }
                            {
                                purchase.data && purchase.data.remainingBalance < 0 ?
                                    <li>
                                        <button
                                            type='button'
                                            className='btn btn-danger px-3'
                                            onClick={handleRefundPayment}
                                        >
                                            Refund Payment
                                        </button>
                                    </li> : null
                            }
                            <li>
                                <div className='btn-group'>
                                    <button
                                        type='button'
                                        className='btn btn-alt px-3 dropdown-toggle'
                                        data-toggle='dropdown'
                                        aria-haspopup='true'
                                        aria-expanded='false'
                                    >
                                        <span className='mr-1'>Change</span>
                                    </button>
                                    <div className='dropdown-menu dropdown-menu-right'>
                                        <button
                                            type='button'
                                            className='dropdown-item'
                                            onClick={() => handleModeChange('update')}
                                        >
                                            Reopen
                                        </button>
                                        {
                                            purchase.data.totalPaid !== 0 ?
                                                <>
                                                    {
                                                        purchase.data.remainingBalance >= 0 && purchase.data.balanceType !== 'OverPaid' ?
                                                            <button
                                                                type='button'
                                                                className='dropdown-item text-danger fw-500'
                                                                onClick={handleRefundPurchase}
                                                            >
                                                                Refund entire invoice
                                                            </button> : null
                                                    }
                                                    {
                                                        purchase.data.totalPaid >= 0 ?
                                                            <button
                                                                type='button'
                                                                className='dropdown-item text-danger fw-500'
                                                                onClick={handleRefundPayment}
                                                            >
                                                                Refund payment
                                                            </button> : null
                                                    }
                                                </> :
                                                <>
                                                    {
                                                        !purchase.data.isNoCharge && !purchase.data.isUncollectible ?
                                                            <>
                                                                <button
                                                                    type='button'
                                                                    className='dropdown-item'
                                                                    onClick={handleNoChargePurchase}
                                                                >
                                                                    No charge
                                                                </button>
                                                            </> : null
                                                    }
                                                    {
                                                        (!purchase.data.payments || purchase.data.payments.length === 0) && !purchase.data.isUncollectible ?
                                                            <button
                                                                type='button'
                                                                className='dropdown-item text-danger fw-500'
                                                                onClick={handleVoidPurchase}
                                                            >
                                                                Void transaction
                                                            </button> : null
                                                    }
                                                </>
                                        }
                                        {
                                            !purchase.data.isNoCharge && !purchase.data.isUncollectible && purchase.data.remainingBalance > 0 && moment(purchase.data.dueDate).startOf('day').add(30, 'days').isBefore(moment().startOf('day')) ?
                                                <button
                                                    type='button'
                                                    className='dropdown-item'
                                                    onClick={handleUncollectiblePurchase}
                                                >
                                                    <span className='fw-500 text-danger'>Uncollectible</span>
                                                </button> : null
                                        }
                                        {
                                            ah.check(AccessType.UPDATE_PRODUCT) ?
                                                <>
                                                    <div className='dropdown-divider'></div>
                                                    <button
                                                        type='button'
                                                        className='dropdown-item'
                                                        onClick={handleUpdateCost}
                                                    >
                                                        Update Cost
                                                    </button>
                                                </> : null
                                        }
                                    </div>
                                </div>
                            </li>
                        </>

                case 'Refunded':
                    return <>
                        <li>
                            <span className='fw-500 text-danger mr-2'>This invoice was refunded on {sys.getFormattedShortDate(moment.utc(purchase.data.createdDateUtc).local())}.</span>
                        </li>
                        {
                            purchase.data.originalPurchaseId ?
                                <li>
                                    <button
                                        type='button'
                                        className='btn btn-danger px-3'
                                        onClick={(e) => { handleOpenRefundedPurchase(e, purchase.data.originalPurchaseId) }}
                                    >
                                        Open Original Invoice
                                    </button>
                                </li> : null
                        }
                    </>

                case 'Voided':
                    return <>
                        {
                            purchase.data.userId && purchase.data.voidedDateUtc && moment.utc(purchase.data.voidedDateUtc).local().startOf('day').isAfter(moment(purchase.data.invoiceDate).startOf('day')) ?
                                <li>
                                    <button
                                        type='button'
                                        className='btn btn-danger px-3'
                                        onClick={handleVoidAdjustment}
                                    >
                                        Clawback
                                    </button>
                                </li> : null
                        }
                    </>

                case 'Uncollectible':
                default:
                    break;
            }
        }
    }

    const renderPaymentButton = () => {
        let defaultHandle = handlePayment;
        const dropdownItems = [];

        if (sys.configuration.purchase.isMultiplePaymentsEnabled && purchase.data.customer && purchase.data.customer.numberOfOutstandingPurchases > 1) {
            if (sys.configuration.purchase.multiplePaymentsDefault === 'all') {
                defaultHandle = handleStartGroupPayment;
                dropdownItems.push(
                    <button
                        type='button'
                        className='dropdown-item'
                        onClick={handlePayment}
                    >
                        This invoice only
                    </button>
                );
            }
            else {
                defaultHandle = handlePayment;
                dropdownItems.push(
                    <button
                        type='button'
                        className='dropdown-item'
                        onClick={handleStartGroupPayment}
                    >
                        All outstanding invoices
                    </button>
                );
            }
        }

        if (sys.configuration.PaymentRequest.isEnabled && purchase.data && purchase.data.customer && purchase.data.customer.defaultEmailAddressContact) {
            dropdownItems.push(
                <button
                    type='button'
                    className='dropdown-item'
                    onClick={handlePaymentRequest}
                >
                    Request Payment
                </button>
            );
        }

        return <>
            {
                dropdownItems && dropdownItems.length > 0 ?
                    <div className='btn-group dropdown'>
                        <button
                            type='button'
                            className='btn btn-alt px-3'
                            onClick={defaultHandle}
                        >
                            Payment
                        </button>
                        <button
                            type='button'
                            className='btn btn-alt dropdown-toggle px-2'
                        >
                        </button>
                        <div className='dropdown-menu dropdown-menu-right min-width-auto'>
                            {
                                dropdownItems.map((d, di) => {
                                    return <Fragment key={`purchase_${purchase.id}_payment_button_${di}`}>{d}</Fragment>
                                })
                            }
                        </div>
                    </div> :
                    <button
                        type='button'
                        className='btn btn-alt px-3'
                        onClick={defaultHandle}
                    >
                        Payment
                    </button>
            }
        </>
    }

    return useObserver(() => <>
        {
            purchase.isReady ?
                <>
                    <LoadingOverlay
                        isLoading={isGeneratingPrint}
                        message={ih.PRINT_MESSAGE}
                    />
                    <DisabledOverlay
                        isLoading={!isGeneratingPrint && (purchase.isLoading || updatePurchase.isLoading || updatePurchase.isSaving)}
                    />
                    {renderTitle()}
                    {renderContent()}
                </> : <>
                    <div className='popup-title'></div>
                    <div className='popup-body'></div>
                </>
        }
        <BodyEnd>
            <CommunicationModal ref={communicationModalRef} onSent={handleCommunicationSent} />
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>)
}

export default ViewPurchaseContent;